import React, { createContext, useState, useContext, useEffect } from 'react';
import { superAdminAPI } from '../services/garageApi';
import { useAuth } from './AuthContext';
import { secureStorage } from '../utils/securityUtils';

const TenantContext = createContext();

export const useTenant = () => useContext(TenantContext);

export const TenantProvider = ({ children }) => {
  const { user, tenant: authTenant, isSuperAdmin } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  // Initialize as empty array to prevent "not an array" errors
  const [availableTenants, setAvailableTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isSuperAdmin) {
      // For super admin, load available tenants
      loadSuperAdminData();
    } else if (authTenant) {
      // For regular users, set their tenant
      setTenant(authTenant);
      setSelectedTenant(authTenant);
      setLoading(false);
    } else if (user?.tenant_id) {
      // Fallback for users with tenant_id
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [authTenant, user, isSuperAdmin]);

  const loadSuperAdminData = async () => {
    try {
      setLoading(true);
      console.log('🔍 TenantContext: Loading super admin data...');
      
      // Load available tenants for super admin
      const response = await superAdminAPI.getAllTenants();
      console.log('🔍 TenantContext: Full API response:', response);
      
      let tenantsData = [];
      
      // Your API returns: { success: true, data: { data: [...], current_page: 1, ... } }
      // So we need to access response.data.data.data for the actual tenants array
      if (response && response.data) {
        // Check for success flag first
        if (response.data.success === false) {
          console.error('❌ TenantContext: API returned success: false');
          setAvailableTenants([]);
          return;
        }
        
        // Handle the nested Laravel pagination structure
        if (response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
          tenantsData = response.data.data.data;
          console.log('✅ TenantContext: Found tenants in response.data.data.data (Laravel pagination)');
        }
        // Alternative: if the structure is response.data.data (array)
        else if (response.data.data && Array.isArray(response.data.data)) {
          tenantsData = response.data.data;
          console.log('✅ TenantContext: Found tenants in response.data.data (direct array)');
        }
        // Fallback: check if response.data itself is an array
        else if (Array.isArray(response.data)) {
          tenantsData = response.data;
          console.log('✅ TenantContext: Found tenants in response.data (direct array)');
        }
        else {
          console.warn('⚠️ TenantContext: Unexpected response structure');
          console.warn('Response structure:', JSON.stringify(response.data, null, 2));
        }
      }
      
      console.log('🔍 TenantContext: Extracted tenants:', tenantsData);
      console.log('🔍 TenantContext: Tenants count:', tenantsData.length);
      console.log('🔍 TenantContext: First tenant:', tenantsData[0]);
      
      // Set the tenants data
      setAvailableTenants(tenantsData);
      
      // Check if super admin already has an active tenant
      const activeTenantId = secureStorage.getItem('activeTenantId');
      if (activeTenantId && tenantsData.some(t => t.id === activeTenantId)) {
        setSelectedTenant(activeTenantId);
        console.log('🔍 TenantContext: Restored active tenant:', activeTenantId);
      }
      
    } catch (error) {
      console.error('❌ TenantContext: Error loading super admin data:', error);
      console.error('❌ TenantContext: Error details:', {
        message: error.message,
        response: error.response,
        data: error.response?.data
      });
      setAvailableTenants([]);
    } finally {
      setLoading(false);
    }
  };

  // Simple tenant switching for super admin
  const switchToTenant = async (tenantId) => {
    try {
      if (!isSuperAdmin) {
        console.warn('Tenant switching not allowed for regular users');
        return;
      }
      
      console.log('🔍 TenantContext: Attempting to switch to tenant:', tenantId);
      
      const response = await superAdminAPI.switchToTenant(tenantId);
      console.log('🔍 TenantContext: Switch response:', response);
      
      if (response.data.success) {
        setSelectedTenant(tenantId);
        secureStorage.setItem('activeTenantId', tenantId);
        console.log('✅ TenantContext: Successfully switched to tenant:', tenantId);
      } else {
        console.error('❌ TenantContext: Backend returned success: false');
        console.error('❌ TenantContext: Error message:', response.data.message);
        
        // Show user-friendly error message
        alert(`Failed to switch tenant: ${response.data.message || 'Unknown error'}`);
        return;
      }
    } catch (error) {
      console.error('❌ TenantContext: Failed to switch tenant:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || 'Server error occurred';
        console.error('❌ TenantContext: Server error:', error.response.status, errorMessage);
        alert(`Server error: ${errorMessage}`);
      } else if (error.request) {
        // Request was made but no response received
        console.error('❌ TenantContext: No response received from server');
        alert('No response from server. Please check your connection.');
      } else {
        // Something else happened
        console.error('❌ TenantContext: Request setup error:', error.message);
        alert(`Request error: ${error.message}`);
      }
    }
  };

  // Clear tenant selection
  const clearTenantSelection = () => {
    if (isSuperAdmin) {
      secureStorage.removeItem('activeTenantId');
      setSelectedTenant(null);
      console.log('🔍 TenantContext: Cleared tenant selection');
    }
  };

  const value = {
    tenant: selectedTenant || tenant,
    selectedTenant,
    availableTenants: availableTenants || [], // Always provide an array
    loading,
    isSuperAdmin,
    switchToTenant,
    clearTenantSelection,
    hasActiveTenantContext: isSuperAdmin ? !!secureStorage.getItem('activeTenantId') : !!tenant,
    getActiveTenantId: () => {
      if (isSuperAdmin) {
        return secureStorage.getItem('activeTenantId');
      }
      return tenant?.id || selectedTenant?.id;
    }
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};